import React from 'react';

const Terms: React.FC = () => {
  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="pt-8 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-3">
            Terms of Service
          </h1>
          <p className="text-lg text-slate-700 dark:text-dark-300">
            Last updated: December 1, 2024
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-8 md:p-12 prose prose-slate dark:prose-invert max-w-none">
            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4">Agreement to Terms</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              These Terms of Service ("Terms") govern your access to and use of the services provided by Cimmplex Innovations LLP ("Company," "we," "our," or "us"), including our website, CimmpleFlow, CimmplePay, and any related services (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Description of Services</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              Cimmplex Innovations LLP provides cloud-based software solutions including:
            </p>
            <ul className="list-disc list-inside text-slate-700 dark:text-dark-300 mb-6 space-y-2">
              <li><strong>CimmpleFlow:</strong> A cloud operating system and manufacturing platform for machine shops</li>
              <li><strong>CimmplePay:</strong> A payroll management solution for businesses</li>
              <li><strong>Custom Development Services:</strong> Tailored software solutions for specific business needs</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">User Accounts</h2>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Registration</h3>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              To access certain features of our Services, you must register for an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-slate-700 dark:text-dark-300 mb-6 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Account Security</h3>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              You are responsible for safeguarding your password and any activities or actions under your account. We are not liable for any loss or damage arising from your failure to maintain the security of your account.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Subscription and Payment</h2>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Pricing and Billing</h3>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              Our Services are offered on a subscription basis. By subscribing, you agree to:
            </p>
            <ul className="list-disc list-inside text-slate-700 dark:text-dark-300 mb-6 space-y-2">
              <li>Pay all fees associated with your chosen subscription plan</li>
              <li>Provide valid payment information</li>
              <li>Authorize us to charge your payment method on a recurring basis</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Free Trial</h3>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              We may offer a free trial period for new users. The free trial provides access to CimmpleFlow features with certain limitations. At the end of the trial period, you must subscribe to continue using the Services.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Refunds</h3>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              Subscription fees are generally non-refundable. However, we may consider refund requests on a case-by-case basis at our discretion.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Acceptable Use</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              You agree not to use the Services to:
            </p>
            <ul className="list-disc list-inside text-slate-700 dark:text-dark-300 mb-6 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Transmit harmful code, malware, or viruses</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Services</li>
              <li>Collect or harvest user data without consent</li>
              <li>Use the Services for any illegal or unauthorized purpose</li>
              <li>Resell or redistribute the Services without authorization</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Intellectual Property</h2>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Our Rights</h3>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              The Services and all content, features, and functionality are owned by Cimmplex Innovations LLP and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written consent.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Your Content</h3>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              You retain ownership of any data or content you upload to the Services. By uploading content, you grant us a limited license to use, store, and process such content solely for the purpose of providing the Services to you.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Data and Privacy</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              Your use of the Services is also governed by our Privacy Policy. By using the Services, you consent to our collection and use of data as described in the Privacy Policy.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Service Availability</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              We strive to maintain high availability of our Services but do not guarantee uninterrupted access. We may temporarily suspend Services for maintenance, updates, or circumstances beyond our control.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Limitation of Liability</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, CIMPLEX INNOVATIONS LLP SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICES.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Disclaimer of Warranties</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Indemnification</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              You agree to indemnify, defend, and hold harmless Cimmplex Innovations LLP and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Services or violation of these Terms.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Termination</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              We may terminate or suspend your access to the Services immediately, without prior notice, for any breach of these Terms. Upon termination, your right to use the Services will immediately cease. You may terminate your account at any time by contacting us.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Governing Law</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Cimmplex Innovations LLP is registered, without regard to its conflict of law provisions.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Changes to Terms</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the updated Terms on our website and updating the "Last updated" date. Your continued use of the Services after changes become effective constitutes acceptance of the revised Terms.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Severability</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-6">
              If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
            </p>

            <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4 mt-8">Contact Us</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-slate-700 dark:text-dark-300">
              <strong>Cimmplex Innovations LLP</strong><br />
              Email: <a href="mailto:contact@cimmple.com" className="text-primary-600 dark:text-primary-400 hover:underline">contact@cimmple.com</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;

